import { useQuery } from '@tanstack/react-query';

import { getUserManagementGateway } from '@/userManagement/application/getUserManagementGateway';
import type { DatabaseRecord } from '@/userManagement/application/types';
import { USER_KEYS } from './queryKeys';

export interface DatabaseListItem {
  /** Backend-canonical database identifier (raw `ra…` name). Sent back to the Sieve filter wire. */
  name: string;

  /** Display-only friendly name from `CustomerDatabase.DBListAlias`. May be null when no alias is set. */
  alias: string | null;
}

export interface UseGetDatabasesResult {
  /** Tenant databases, sorted by `alias ?? name` for display. */
  databases: DatabaseListItem[];
  isLoading: boolean;
  error: Error | null;
}

function mapAndSortDatabaseRecords(records: DatabaseRecord[]): DatabaseListItem[] {
  return records
    .map<DatabaseListItem>((r) => ({ name: r.name, alias: r.alias ?? null }))
    .sort((a, b) => (a.alias ?? a.name).localeCompare(b.alias ?? b.name, undefined, { sensitivity: 'base' }));
}

/**
 * TROC-138: full tenant database catalog for alias lookups and non–Add User surfaces.
 */
export function useGetDatabases(): UseGetDatabasesResult {
  const { data, isLoading, error } = useQuery({
    queryKey: USER_KEYS.databaseCatalog(),
    queryFn: async () => mapAndSortDatabaseRecords(await getUserManagementGateway().getDatabases()),
    retry: false
  });

  return {
    databases: data ?? [],
    isLoading,
    error: error instanceof Error ? error : null
  };
}

/**
 * TROC-211: Users-tab Databases filter — scoped list from `GET /databases?scope=usersTabFilter`.
 */
export function useGetUsersTabDatabaseFilterOptions(): UseGetDatabasesResult {
  const { data, isLoading, error } = useQuery({
    queryKey: USER_KEYS.databaseCatalogUsersTabFilter(),
    queryFn: async () => mapAndSortDatabaseRecords(await getUserManagementGateway().getDatabasesForUsersTabFilter()),
    retry: false
  });

  return {
    databases: data ?? [],
    isLoading,
    error: error instanceof Error ? error : null
  };
}

/**
 * TROC-363: Add User active catalog — `GET /databases?scope=activeCatalog`
 * (CDC-allowlisted tenant DBs, including empty DBs with no synced users).
 */
export function useGetActiveCatalogDatabases(): UseGetDatabasesResult {
  const { data, isLoading, error } = useQuery({
    queryKey: USER_KEYS.databaseCatalogActiveCatalog(),
    queryFn: async () => mapAndSortDatabaseRecords(await getUserManagementGateway().getDatabasesForActiveCatalog()),
    retry: false
  });

  return {
    databases: data ?? [],
    isLoading,
    error: error instanceof Error ? error : null
  };
}
